import { useCallback, useRef, useState } from "react";
import { toggleMenu } from "../../../../utils/helpers.js";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import { employeeFlag } from "../../../../constants/employeeFlag.js";
import { getUser, logoutUser } from "../../../../utils/Storage";
import { useBreadcrumbs } from "../../../context/breadcrumb/breadcrumb-context.tsx";
import styles from "./header.module.css";
import { useProfileImage } from "../../../hooks/use-profile-image.tsx";
import { useDismissOutside } from "../../../hooks/use-dismiss-outside.ts";
import { Dropdown } from "../../ui/dropdown/dropdown.tsx";
import { Button, ButtonLink } from "../../ui/button/button.tsx";
import { Avatar } from "../../ui/index.ts";
import { useCsatStars } from "../../../hooks/use-csat-stars.ts";
import { formatNavigationLabel } from "../../../utils/sqe-labs-navigation-access.ts";
import { useSqeLabs } from "../../../hooks/use-sqe-labs.ts";
interface HeaderProps {
  title?: string;
  description?: string;
  isHome?: boolean;
  userName?: string;
}
export const Header = ({ title, description, isHome }: HeaderProps) => {
  const userData = JSON.parse(getUser("userData"));
  const userName = userData?.name || "User";
  const isSqeLabs = useSqeLabs();
  const headerTitle = formatNavigationLabel(title, isSqeLabs ? "SQELABS" : "");
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const userMenuPanelRef = useRef<HTMLDivElement>(null);
  const dismissUserMenu = useCallback(() => setShowUserMenu(false), []);

  useDismissOutside({
    ref: userMenuRef,
    isOpen: showUserMenu,
    onDismiss: dismissUserMenu,
    extraRefs: [userMenuPanelRef],
  });

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 17) return "Good Afternoon";
    return "Good Evening";
  };
  const { breadcrumbs } = useBreadcrumbs();
  const profileImage = useProfileImage(userData?.id);
  const { goldStars, silverStars } = useCsatStars(!isSqeLabs);
  const hasStars = goldStars > 0 || silverStars > 0;
  return (
    <header className={`${styles.header} sticky-top`}>
      <div id="showMenuBtn" className="collapse-button" onClick={toggleMenu}>
        <span></span>
        <span></span>
        <span></span>
      </div>
      {/* LEFT SIDE */}
      {isHome ? (
        <div className={styles.headerMain}>
          <h2 className="heading-lg">
            {getGreeting()}, {userName}
          </h2>
          <p className="sub-heading-sm">{description}</p>

          {!isSqeLabs && hasStars && (
            <div className={`${styles.csatStarsDiv}`}>
              <p className="mb-0">CSAT Stars:</p>

              {goldStars > 0 && (
                <div className={styles.starWrapper}>
                  <img
                    src="/assets/images/star.png"
                    alt="gold star"
                    className={styles.csatGoldStar}
                  />
                  <span className={styles.starBadge}>{goldStars}</span>
                </div>
              )}

              {(silverStars > 0 || goldStars > 0) && (
                <div className={styles.starWrapper}>
                  <img
                    src="/assets/images/silver_star.png"
                    alt="silver star"
                    className={styles.csatSilverStar}
                  />
                  <span className={styles.starBadge}>{silverStars}</span>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className={styles.headerMain}>
          {breadcrumbs?.length > 0 ? (
            <Breadcrumbs items={breadcrumbs} />
          ) : (
            <>
              <h2 className="heading-lg">{headerTitle}</h2>
              {description && <p className="sub-heading-sm">{description}</p>}
            </>
          )}
        </div>
      )}

      {/* RIGHT SIDE (COMMON FOR BOTH) */}
      <div className={styles.headerActions}>
        <div id="page-header-actions" className={styles.pageActionsSlot} />
        {userData?.location === employeeFlag.CCI_US ? (
          <img
            className={styles.regionFlag}
            src="/assets/us-flag.svg"
            alt="US flag"
          />
        ) : (
          <img
            className={styles.regionFlag}
            src="/assets/indian-flag.svg"
            alt="India flag"
          />
        )}

        <div ref={userMenuRef} className={styles.profileMenuWrap}>
          <Dropdown
            open={showUserMenu}
            placement="bottom"
            panelMinWidth={220}
            panelAlign="end"
            shrinkWrap
            className={styles.profileMenuRoot}
            panelClassName={styles.profileMenuPanel}
            panelRef={userMenuPanelRef}
            trigger={
              <button
                type="button"
                className={styles.avatarBtn}
                aria-label={`Account menu, ${userName}`}
                aria-expanded={showUserMenu}
                aria-haspopup="menu"
                onClick={() => setShowUserMenu((p) => !p)}
              >
                <Avatar name={userName} src={profileImage} size="md" />
              </button>
            }
          >
            <div className={styles.profileMenuStack}>
              <ButtonLink
                to="/dashboard/myProfile"
                variant="accent-ghost"
                fullWidth
                align="start"
                className={styles.profileMenuAction}
                onClick={() => setShowUserMenu(false)}
              >
                My Profile
              </ButtonLink>
              <Button
                type="button"
                variant="danger"
                fullWidth
                align="start"
                className={styles.profileMenuAction}
                onClick={() => {
                  setShowUserMenu(false);
                  logoutUser();
                }}
              >
                Log Out
              </Button>
            </div>
          </Dropdown>
        </div>
      </div>
    </header>
  );
};
